// The Shipping hub (Phase 1, 2026-10-27; behind GET /features shipping_hub). One screen for the shipping floor:
// a search box (type or scan a PLT / BCN / SO / customer / PO) and the "Working as" chip on top, then
//   Pack · Ready to ship · Ready for pickup · Out for delivery   from GET /shipping/so-board
//     (Pack lists only pallets ready to seal / holding pallets ready to pack; below it, collapsed, "Not ready to pack
//     yet" lists the orders whose pallets are at a vendor, to coating, at a station or still being packed)
//   Outsourcing    the coating desk in operator mode
//   Ship by job    the old Ready to Ship queue (orders with a packing plan ship from their package guide)
//   History        the old Shipped Items
// Each order row has ONE big button. ?tab= keeps the tab; ?so=&pallet= opens that order's package guide.
//
// Phases 2-4 (2026-10-28), each behind its GET /features shipping_signing switch (`signing`; off = Phase 1 exactly):
//   pickup     Start pickup (and the package guide's Confirm Pickup) = the load-out checklist, then the customer signs
//              on a phone (./pickupSigning -> features/signing/SignRequestPanel), or on paper
//   delivery   Ready to ship: a delivery order's button is Load truck (./LoadTruckModal); Out for delivery shows the
//              load (driver, truck, loaded at / by) and its signature, with Show signing QR + Manifest; History lists
//              the loads (./LoadsHistory). An order out with no live code (shipped the plain way, or it expired) gets
//              Show signing QR = Start delivery signing (./deliverySigning); the package guide's Ship pallet / Ship all
//              open Load truck for a delivery order (features/packageGuide/PackageGuideCard)
//   job_loads  History lists the loads too (Ship by job's own load box is in pages/ShippingQueuePage)
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import WorkingAsChip from '../operatorSession/WorkingAsChip';
import PackageGuidePanel from './PackageGuidePanel';
import { HUB_TABS, readHubUrl, writeHubUrl } from './hubUrl';
import {
  dueText, filterOrders, fulfillmentText, lanePallets, laneLines, loadSoBoard, normalizeBoard, notReadyText,
  ordersInLane, palletName, resolveSearchCode, searchTarget,
} from './soBoard';
import { SIGNING_OFF } from './signingFlags';
import LoadTruckModal from './LoadTruckModal';
import LoadsHistory from './LoadsHistory';
import {
  laneRowView, loadedMessage, openLoadManifest as openManifest, showSigningQr as showQr, startDeliveryCode as startCode,
  startPickup as startPickupAction,
} from './hubActions';
import { startSignedPickup } from './pickupSigning';
import { chipClass, loadDriverText, loadVehicleText, loadedText, signChips } from '../signing/signWords';

const EMPTY = normalizeBoard(null);

const styles = {
  header: { display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 16 },
  title: { margin: 0, marginRight: 'auto' },
  search: { minHeight: 48, fontSize: '1.1rem', minWidth: 260, flex: '1 1 320px', maxWidth: 520 },
  tab: { minHeight: 48, fontSize: '1.02rem', padding: '10px 14px' },
  row: { display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' },
  so: { fontSize: '1.3rem', fontWeight: 700, lineHeight: 1.2 },
  lines: { flex: '1 1 260px', minWidth: 0 },
  action: { minHeight: 52, minWidth: 180, fontSize: '1.1rem', fontWeight: 600 },
  toggle: { minHeight: 48, fontSize: '1.05rem', width: '100%', textAlign: 'left' },
  muted: { opacity: 0.85, background: 'var(--bs-light, #f8f9fa)' },
};

const EMPTY_TEXT = {
  pack: 'Nothing ready to pack right now.',
  ready: 'No sealed pallets waiting to leave.',
  pickup: 'No pallets waiting for a customer.',
  out: 'Nothing out on our truck.',
};

/**
 * Props: apiBase; ui (the Working-as ui: workingAsUi(desktopUi)); onBoard(board) after each read; renderOutsourcing(),
 * renderByJob({board}), renderHistory(): the tabs the page draws itself; signing: the shipping_signing switches
 * (./signingFlags; default all off).
 */
export default function ShippingHub({ apiBase, ui, onBoard, renderOutsourcing, renderByJob, renderHistory, signing = SIGNING_OFF }) {
  const [place, setPlace] = useState(() => readHubUrl(window.location.search));
  const [board, setBoard] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [query, setQuery] = useState('');
  const [message, setMessage] = useState(null); // {text, ok}
  const [busyId, setBusyId] = useState(null);
  const [showNotReady, setShowNotReady] = useState(false);
  const [loadOrder, setLoadOrder] = useState(null); // the order whose Load truck dialog is open
  const readSeq = useRef(0);
  const onBoardRef = useRef(onBoard);
  onBoardRef.current = onBoard;

  const reload = useCallback(async () => {
    const seq = ++readSeq.current;
    setLoading(true);
    const r = await loadSoBoard(apiBase);
    if (seq !== readSeq.current) return;
    setLoading(false);
    if (r.ok) {
      setBoard(r.board);
      setLoadError('');
      if (onBoardRef.current) onBoardRef.current(r.board);
    } else setLoadError(r.message);
  }, [apiBase]);

  useEffect(() => { reload(); }, [reload]);

  // Back / Forward move between hub places
  useEffect(() => {
    const onPop = () => setPlace(readHubUrl(window.location.search));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // the address bar always says where the hub is (a bare /shipping-queue gets ?tab=pack)
  useEffect(() => { writeHubUrl(place); }, [place]);

  function go(next, { push = false } = {}) {
    const p = { ...place, ...next };
    if (push) writeHubUrl(p, { push: true });
    setPlace(p);
  }
  const openTab = (tab) => { setMessage(null); go({ tab, so: null, pallet: null }); if (HUB_TABS.find((t) => t.id === tab)?.lane) reload(); };
  const openGuide = (so, pallet = null) => go({ so, pallet }, { push: true });
  const closeGuide = () => { go({ so: null, pallet: null }); reload(); };

  async function onSearchKey(e) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const t = searchTarget(board, query);
    if (t.filter !== undefined) return;
    if (t.open) { setQuery(''); openGuide(t.open.so, t.open.pallet); return; }
    const r = await resolveSearchCode(apiBase, t.resolve);
    if (r.open) { setQuery(''); openGuide(r.open.so, r.open.pallet); return; }
    setMessage({ text: r.message, ok: false });
  }

  // the row actions: ONE copy with the phone Shipping screen (./hubActions)
  const busyFor = (o) => (on) => setBusyId(on ? o.plan_id : null);
  const say = (m) => { if (m) setMessage(m); };

  async function startPickup(order) {
    if (busyId != null) return;
    setMessage(null);
    say(await startPickupAction({ apiBase, order, ui, signing, setBusy: busyFor(order), reload }));
  }

  // the package guide's Confirm Pickup in operator mode, signed by the customer (shipping_signing.pickup)
  const signedPickup = signing.pickup
    ? ({ plan, palletIds = null, setBusy }) => startSignedPickup({ apiBase, plan, palletIds, ui, setBusy })
    : null;

  function closeLoadTruck(result) {
    const o = loadOrder;
    setLoadOrder(null);
    if (result && result.loaded) {
      setMessage(loadedMessage(o));
      reload();
    }
  }

  async function showSigningQr(order) {
    if (busyId != null) return;
    setMessage(null);
    say(await showQr({ apiBase, order, ui, setBusy: busyFor(order), reload }));
  }

  async function startDeliveryCode(order) {
    if (busyId != null) return;
    setMessage(null);
    say(await startCode({ apiBase, order, ui, setBusy: busyFor(order), reload }));
  }

  async function openLoadManifest(order) {
    say(await openManifest({ apiBase, order, ui }));
  }

  const filterText = searchTarget(board, query).filter !== undefined ? query : '';
  const laneOrders = useMemo(() => {
    const out = {};
    ['pack', 'ready', 'pickup', 'out', 'not_ready'].forEach((l) => { out[l] = filterOrders(ordersInLane(board, l), filterText); });
    return out;
  }, [board, filterText]);

  const guideOrder = place.so ? board.orders.find((o) => Number(o.sales_order_id) === Number(place.so)) || null : null;
  const tabLabel = (HUB_TABS.find((t) => t.id === place.tab) || HUB_TABS[0]).label;

  function renderLane(lane) {
    const rows = laneOrders[lane];
    if (loading && !board.orders.length) {
      return <div className="text-center p-4"><div className="spinner-border text-primary" /></div>;
    }
    if (!rows.length) {
      return <div className="alert alert-secondary" style={{ fontSize: '1.05rem' }}>
        {filterText ? `Nothing here matches "${filterText}".` : EMPTY_TEXT[lane]}
      </div>;
    }
    return (
      <div className="d-flex flex-column gap-2" data-testid={`hub-lane-${lane}`}>
        {rows.map((o) => {
          const { due, act, outInfo, qrPending, needCode, firstPalletId } = laneRowView(o, lane, signing);
          const busy = busyId === o.plan_id;
          return (
            <div className="card" key={`${lane}-${o.plan_id}`} data-testid="hub-order-row">
              <div className="card-body" style={styles.row}>
                <div style={{ minWidth: 180 }}>
                  <div style={styles.so}>{o.order_number}</div>
                  <div>{o.customer_name}{o.customer_short_code ? <span className="text-muted"> · {o.customer_short_code}</span> : null}</div>
                  {o.customer_po && <div className="small text-muted">PO {o.customer_po}</div>}
                </div>
                <div style={styles.lines}>
                  <div className="mb-1">
                    <span className="badge bg-light text-dark border me-2">{fulfillmentText(o.fulfillment_type)}</span>
                    <span className={due.late ? 'text-danger fw-semibold' : 'text-muted'}>{due.text}</span>
                  </div>
                  {laneLines(o, lane).map((l) => <div key={l}>{l}</div>)}
                  {outInfo && <OutLoadInfo order={o} />}
                </div>
                <div className="d-flex flex-column align-items-end gap-1">
                  {qrPending || needCode ? (
                    <button type="button" className="btn btn-primary" style={styles.action} disabled={busyId != null}
                      data-testid={needCode ? 'hub-start-delivery-qr' : undefined}
                      onClick={() => (needCode ? startDeliveryCode(o) : showSigningQr(o))}>
                      {busy ? <span className="spinner-border spinner-border-sm me-2" /> : null}
                      Show signing QR
                    </button>
                  ) : (
                    <button type="button"
                      className={`btn ${act.kind === 'pickup' ? 'btn-success' : lane === 'ready' ? 'btn-primary' : 'btn-outline-primary'}`}
                      style={styles.action}
                      disabled={busyId != null}
                      onClick={() => {
                        if (act.kind === 'pickup') startPickup(o);
                        else if (act.kind === 'load') { setMessage(null); setLoadOrder(o); }
                        else openGuide(o.sales_order_id, firstPalletId);
                      }}>
                      {busy ? <span className="spinner-border spinner-border-sm me-2" /> : null}
                      {act.label}
                    </button>
                  )}
                  {outInfo && o.load && o.load.load_id != null && (
                    <button type="button" className="btn btn-outline-secondary" style={{ minHeight: 44, minWidth: 180 }}
                      onClick={() => openLoadManifest(o)}>
                      Manifest
                    </button>
                  )}
                  {(act.kind === 'pickup' || act.kind === 'load' || qrPending || needCode) && (
                    <button type="button" className="btn btn-link p-0" style={{ minHeight: 44 }}
                      onClick={() => openGuide(o.sales_order_id, firstPalletId)}>
                      Open package guide
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Below Pack, collapsed: orders with pallets on the board that can't be packed yet, each pallet's reason
  function renderNotReady() {
    const rows = laneOrders.not_ready;
    if (!rows.length) return null;
    return (
      <div className="mt-4" data-testid="hub-not-ready">
        <button type="button" className="btn btn-outline-secondary" style={styles.toggle}
          aria-expanded={showNotReady} aria-controls="hub-not-ready-list"
          onClick={() => setShowNotReady((v) => !v)}>
          {showNotReady ? '▾' : '▸'} Not ready to pack yet ({rows.length})
        </button>
        {showNotReady && (
          <div id="hub-not-ready-list" className="d-flex flex-column gap-2 mt-2" data-testid="hub-lane-not_ready">
            {rows.map((o) => {
              const due = dueText(o.promised_date);
              const ps = lanePallets(o, 'not_ready');
              return (
                <div className="card text-muted" style={styles.muted} key={`nr-${o.plan_id}`} data-testid="hub-not-ready-row">
                  <div className="card-body" style={styles.row}>
                    <div style={{ minWidth: 180 }}>
                      <div style={{ ...styles.so, fontSize: '1.15rem' }}>{o.order_number}</div>
                      <div>{o.customer_name}{o.customer_short_code ? <span> · {o.customer_short_code}</span> : null}</div>
                    </div>
                    <div style={styles.lines}>
                      <div className="mb-1">
                        <span className="badge bg-light text-dark border me-2">{fulfillmentText(o.fulfillment_type)}</span>
                        <span className={due.late ? 'text-danger fw-semibold' : ''}>{due.text}</span>
                      </div>
                      {laneLines(o, 'not_ready').map((l) => <div key={l}>{l}</div>)}
                      {ps.map((p) => {
                        const why = notReadyText(p);
                        return (
                          <div key={p.pallet_id} data-testid="hub-not-ready-reason">
                            <span className="fw-semibold">{palletName(p)}</span>: {why.text}
                            {why.outsourcing && (
                              <button type="button" className="btn btn-link p-0 ms-2 align-baseline" style={{ minHeight: 44 }}
                                onClick={() => openTab('outsourcing')}>
                                Go to Outsourcing
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="d-flex flex-column align-items-end gap-1">
                      <button type="button" className="btn btn-outline-secondary" style={{ minHeight: 48, minWidth: 120 }}
                        disabled={busyId != null}
                        onClick={() => openGuide(o.sales_order_id, ps[0] ? ps[0].pallet_id : null)}>
                        Open
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  const counts = board.counts;
  return (
    <div className="container-fluid my-3" style={{ maxWidth: 1400 }} data-testid="shipping-hub">
      <div style={styles.header}>
        <h1 className="text-primary" style={styles.title}>Shipping</h1>
        <input
          type="search"
          className="form-control"
          style={styles.search}
          data-own-scans="true"
          aria-label="Find an order or pallet"
          placeholder="Scan or type a PLT, BCN, SO, customer or PO"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onSearchKey}
        />
        <WorkingAsChip ui={ui} />
      </div>

      {place.so ? (
        <PackageGuidePanel salesOrderId={place.so} palletId={place.pallet} order={guideOrder} apiBase={apiBase} ui={ui}
          backLabel={`Back to ${tabLabel}`} onClose={closeGuide} signedPickup={signedPickup} />
      ) : (
        <>
          <ul className="nav nav-tabs mb-3" role="tablist">
            {HUB_TABS.map((t) => {
              const n = t.lane ? counts[t.id] : 0;
              return (
                <li className="nav-item" key={t.id}>
                  <button type="button" role="tab" aria-selected={place.tab === t.id}
                    className={`nav-link ${place.tab === t.id ? 'active' : ''}`} style={styles.tab}
                    onClick={() => openTab(t.id)}>
                    {t.label}
                    {n > 0 && <span className={`badge ms-2 ${place.tab === t.id ? 'bg-primary' : 'bg-secondary'}`}>{n}</span>}
                  </button>
                </li>
              );
            })}
            {place.tab !== 'outsourcing' && (
              // the Outsourcing tab has its own Refresh (it re-reads the manifests, not the board)
              <li className="nav-item ms-auto">
                <button type="button" className="btn btn-outline-secondary" style={{ minHeight: 44 }} onClick={reload} disabled={loading}>
                  ↻ Refresh
                </button>
              </li>
            )}
          </ul>

          {message && (
            <div className={`alert ${message.ok ? 'alert-success' : 'alert-danger'} d-flex justify-content-between align-items-start`} role="status">
              <span>{message.text}</span>
              <button type="button" className="btn-close ms-2" aria-label="Dismiss" onClick={() => setMessage(null)} />
            </div>
          )}
          {loadError && HUB_TABS.find((t) => t.id === place.tab)?.lane && (
            <div className="alert alert-danger d-flex justify-content-between align-items-center">
              <span>Couldn't load the orders: {loadError}</span>
              <button type="button" className="btn btn-outline-danger" style={{ minHeight: 44 }} onClick={reload}>Try again</button>
            </div>
          )}

          {['pack', 'ready', 'pickup', 'out'].includes(place.tab) && renderLane(place.tab)}
          {place.tab === 'pack' && !(loading && !board.orders.length) && renderNotReady()}
          {place.tab === 'outsourcing' && renderOutsourcing && renderOutsourcing()}
          {place.tab === 'byjob' && renderByJob && renderByJob({ board })}
          {place.tab === 'history' && (signing.delivery || signing.job_loads) && <LoadsHistory apiBase={apiBase} ui={ui} />}
          {place.tab === 'history' && renderHistory && renderHistory()}
        </>
      )}
      {loadOrder && (
        <LoadTruckModal apiBase={apiBase} ui={ui} order={loadOrder} onClose={closeLoadTruck}
          onOpenGuide={() => openGuide(loadOrder.sales_order_id, null)} />
      )}
    </div>
  );
}

/** Out for delivery, with a load: who drives, which truck, loaded when / by whom, and the signature. */
function OutLoadInfo({ order }) {
  const load = order.load || null;
  const driver = loadDriverText(load);
  const truck = loadVehicleText(load);
  const loaded = loadedText(load);
  const chips = signChips(order.sign, { deliveredAt: order.delivered_at || null });
  return (
    <div className="mt-2" data-testid="hub-out-load">
      {(driver || truck) && (
        <div><span className="text-muted">Driver / truck:</span> {[driver, truck].filter(Boolean).join(' · ')}</div>
      )}
      {loaded && <div><span className="text-muted">Loaded:</span> {loaded}</div>}
      {chips.length > 0 && (
        <div className="d-flex flex-wrap gap-1 mt-1" data-testid="hub-sign-chips">
          {chips.map((c) => <span key={c.text} className={chipClass(c.tone)} style={{ fontSize: '0.95rem' }}>{c.text}</span>)}
          {order.sign && order.sign.status === 'pending' && <span className="small text-muted ms-1">QR on the manifest</span>}
        </div>
      )}
    </div>
  );
}
